import { useEffect, useRef, useState } from 'react';
import { BrowserQRCodeReader } from '@zxing/browser';
import React from 'react';

interface QRScannerProps {
  onScan: (result: string) => void;
}

export default function QRScanner({ onScan }: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');

  useEffect(() => {
    const codeReader = new BrowserQRCodeReader();
    let mounted = true;

    const stopStream = () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };

    const startScanning = async () => {
      try {
        // Prefer facingMode and fall back to deviceId selection if necessary
        let stream: MediaStream | null = null;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: facingMode } }
          });
        } catch (firstErr) {
          const videoInputDevices = await BrowserQRCodeReader.listVideoInputDevices();
          if (!videoInputDevices.length) {
            throw new Error('No camera found');
          }
          // Try to choose a device by label hint
          const preferred = videoInputDevices.find(d =>
            facingMode === 'environment'
              ? /back|rear/i.test(d.label)
              : /front|user|face/i.test(d.label)
          ) || (facingMode === 'environment' ? videoInputDevices[videoInputDevices.length - 1] : videoInputDevices[0]);
          const constraints = { video: { deviceId: preferred.deviceId } } as MediaStreamConstraints;
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        }

        if (!stream) {
          throw new Error('Unable to start camera stream');
        }
        streamRef.current = stream;

        if (videoRef.current && mounted) {
          videoRef.current.srcObject = stream;

          codeReader
            .decodeFromVideoElement(videoRef.current, (result, err) => {
              if (result && mounted) {
                const text = result.getText()?.trim();
                if (text && text.length > 0) {
                  console.log("QR code detected:", text);
                  onScan(text);
                  mounted = false;
                  // Stop stream after a successful scan
                  stopStream();
                }
              } else if (err) {
                // Optionally handle decode errors here,
                // console.error('Decode error', err);
              }
            })
            .catch((err) => {
              console.error("QR decode error:", err);
            });
        }
      } catch (err) {
        console.error('Failed to start QR scanner:', err);
      }
    };

    startScanning();

    return () => {
      mounted = false;
      stopStream();
    };
  }, [onScan, facingMode]);

  const turnOffCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const flipCamera = () => {
    setFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'));
  };


  return (
    <div style={{ width: '100%' }}>
      <div style={{ position: 'relative', width: '100%' }}>
        <video
          ref={videoRef}
          style={{ width: '100%', height: 'auto', maxHeight: '60vh', backgroundColor: 'black', borderRadius: 8 }}
          autoPlay
          playsInline
        />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <button type="button" onClick={flipCamera} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', flexShrink: 0 }}>
          Switch Camera ({facingMode === 'environment' ? 'Back' : 'Front'})
        </button>
        <p style={{ margin: 0, fontSize: 12, color: '#6b7280', textAlign: 'right', flex: 1, minWidth: 180 }}>
          Point your camera at a QR code
        </p>
      </div>
    </div>
  );
}
